import { useState, useEffect, useCallback, useRef } from 'react';

export interface Location {
  latitude: number;
  longitude: number;
}

interface UseLocationReturn {
  location: Location | null;
  loading: boolean;
  error: string | null;
  requestLocation: () => Promise<Location | null>;
  locationStatus: 'idle' | 'requesting' | 'granted' | 'denied' | 'unavailable';
}

const STORAGE_KEY = 'bitecritic_user_location';
const PERMISSION_STATUS_KEY = 'bitecritic_location_permission';

export const useLocation = (): UseLocationReturn => {
  const [location, setLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationStatus, setLocationStatus] = useState<
    'idle' | 'requesting' | 'granted' | 'denied' | 'unavailable'
  >('idle');
  const [initialized, setInitialized] = useState(false);
  const autoRequestRef = useRef(false);

  // Define requestLocation function
  const requestLocation = useCallback(async (): Promise<Location | null> => {
    if (typeof window === 'undefined' || !navigator.geolocation) {
      setLocationStatus('unavailable');
      localStorage.setItem(PERMISSION_STATUS_KEY, 'unavailable');
      setError('Geolocation is not supported by your browser');
      return null;
    }

    setLoading(true);
    setError(null);
    setLocationStatus('requesting');

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Validate coordinates
          if (
            isNaN(position.coords.latitude) ||
            isNaN(position.coords.longitude)
          ) {
            setError('Invalid coordinates received');
            setLocationStatus('denied');
            localStorage.setItem(PERMISSION_STATUS_KEY, 'denied');
            setLoading(false);
            resolve(null);
            return;
          }

          const newLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };

          // Save to localStorage
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newLocation));
            localStorage.setItem(PERMISSION_STATUS_KEY, 'granted');
          } catch (e) {
            console.error('Failed to save location to localStorage', e);
          }

          setLocation(newLocation);
          setLocationStatus('granted');
          setLoading(false);
          resolve(newLocation);
        },
        (err) => {
          let errorMessage = 'Failed to get location';

          if (err instanceof GeolocationPositionError) {
            switch (err.code) {
              case err.PERMISSION_DENIED:
                errorMessage = 'Location access was denied';
                setLocationStatus('denied');
                localStorage.setItem(PERMISSION_STATUS_KEY, 'denied');
                break;
              case err.POSITION_UNAVAILABLE:
                errorMessage = 'Location information is unavailable';
                setLocationStatus('unavailable');
                localStorage.setItem(PERMISSION_STATUS_KEY, 'unavailable');
                break;
              case err.TIMEOUT:
                errorMessage = 'Location request timed out';
                setLocationStatus('unavailable');
                localStorage.setItem(PERMISSION_STATUS_KEY, 'unavailable');
                break;
              default:
                setLocationStatus('unavailable');
                localStorage.setItem(PERMISSION_STATUS_KEY, 'unavailable');
            }
          } else {
            setLocationStatus('unavailable');
            localStorage.setItem(PERMISSION_STATUS_KEY, 'unavailable');
          }

          setError(errorMessage);
          setLoading(false);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000, // Increased timeout for slower connections
          maximumAge: 60000, // Allow cached positions up to 1 minute old
        },
      );
    });
  }, []);

  // Load saved location from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      // Try to load location from localStorage
      const savedLocation = localStorage.getItem(STORAGE_KEY);
      if (savedLocation) {
        const parsedLocation = JSON.parse(savedLocation) as Location;
        setLocation(parsedLocation);
      }

      // Check previously saved permission status
      const permissionStatus = localStorage.getItem(PERMISSION_STATUS_KEY);
      if (permissionStatus === 'granted') {
        setLocationStatus('granted');
        // Mark for auto-request but don't call it directly from the effect
        autoRequestRef.current = true;
      } else if (permissionStatus === 'denied') {
        setLocationStatus('denied');
      } else if (permissionStatus === 'unavailable') {
        setLocationStatus('unavailable');
      }
    } catch (e) {
      console.error('Failed to load location from localStorage', e);
    } finally {
      setInitialized(true);
    }
  }, []);

  // Auto-request location if previously granted
  useEffect(() => {
    if (initialized && autoRequestRef.current) {
      autoRequestRef.current = false;
      requestLocation();
    }
  }, [initialized, requestLocation]);

  // Check if geolocation is available
  useEffect(() => {
    if (typeof window !== 'undefined' && !navigator.geolocation) {
      setLocationStatus('unavailable');
      localStorage.setItem(PERMISSION_STATUS_KEY, 'unavailable');
      setError('Geolocation is not supported by your browser');
    }
  }, []);

  return { location, loading, error, requestLocation, locationStatus };
};
